import {
  CareRequestInput,
  CareRequestPlanResponse,
  CareContactRequest,
  CareContactResponse,
  CareSuggestRequest,
  CareSuggestResponse,
  ChatRequest,
  ChatResponse,
  ChatMessage,
  AgentAction,
  EIRData,
  HealthAnalysis,
  HealthAnalysisResponse,
  HealthVideosRequest,
  HealthVideosResponse,
  HealthAction,
  NutritionAnalysisResponse,
  RemoteEhrSessionStartResponse,
  RemoteEhrSessionStatusResponse,
  TranscriptionResponse,
  FeedbackFormDetails,
  FeedbackFormSummary,
  FeedbackInsight,
  FeedbackActionPlan,
  FeedbackSubmissionRequest,
  FeedbackSubmissionResponse,
  FeedbackTransformRequest,
  PublicFeedbackForm,
  RoadmapFeature,
  RoadmapSuggestion,
  RoadmapUpvoteResponse,
  RoadmapSuggestionRequest,
  HealthQuiz,
  QuizResult,
  ChatBlock,
  HealthMemoryContextSnippet,
  HealthMemoryItem,
} from '../types';
import { z } from 'zod';
import {
  CheckoutSessionResponse,
  SubscriptionStatusResponse,
  SubscriptionVerifyResponse,
  PortalSessionResponse
} from '../types/subscription';
import { CONFIG } from '../config';
import { logger } from '../utils/logger';
import { persistence } from 'services/persistence';

const citationSchema = z.object({
  entryId: z.string(),
  reference: z.string(),
});

const chatBlockSchema: z.ZodType<ChatBlock> = z.object({
  id: z.string(),
  type: z.enum(['multiple-choice', 'scale', 'text-input', 'info-card']),
  question: z.string().optional(),
  options: z.array(z.object({
    id: z.string(),
    label: z.string(),
    description: z.string().optional(),
  })).optional(),
  scaleMin: z.number().optional(),
  scaleMax: z.number().optional(),
  scaleLabels: z.object({ min: z.string(), max: z.string() }).optional(),
  placeholder: z.string().optional(),
  infoTitle: z.string().optional(),
  infoBody: z.string().optional(),
  progress: z.object({ current: z.number(), total: z.number() }).optional(),
  response: z.union([z.string(), z.number()]).optional(),
  respondedAt: z.string().optional(),
});

const agentActionSchema = z.object({
  type: z.enum([
    'calendar:add',
    'journal:add',
    'navigation:navigate',
    'care:find',
    'health-actions:add',
    'assessment:suggest',
    'assessment:init',
    'care-request:create',
    'chat:render-block',
  ]),
  status: z.enum(['proposed', 'confirmed', 'rejected', 'executed', 'failed']).optional(),
  payload: z.unknown(),
  validationErrors: z.array(z.object({
    tool: z.string(),
    code: z.string(),
    message: z.string(),
    field: z.string().optional(),
  })).optional(),
});

const unifiedAgentResponseSchema = z.object({
  assistant_message: z.string().optional(),
  ui_blocks: z.array(chatBlockSchema).optional(),
  actions: z.array(agentActionSchema).optional(),
  suggested_followups: z.array(z.string()).optional(),
  citations: z.array(citationSchema).optional(),
  safety_notice: z.string().optional(),
  debug: z.unknown().optional(),
  reply: z.string().optional(),
  timestamp: z.string().optional(),
});

export class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = CONFIG.API_BASE_URL;
  }

  private getProfile(): Record<string, unknown> {
    return persistence.getJSON<Record<string, unknown>>('eir-user-profile', {});
  }

  private getPreferredModel(): string | undefined {
    const profile = this.getProfile();
    const model = typeof profile?.preferredModel === 'string' ? profile.preferredModel : undefined;
    return model && model !== 'auto' ? model : undefined;
  }

  private isAgentModeEnabled(): boolean {
    const profile = this.getProfile();
    return profile?.agentModeEnabled !== false;
  }

  private async request<T>(
    path: string,
    options: {
      method?: 'GET' | 'POST' | 'DELETE';
      body?: unknown;
      rawBody?: BodyInit;
      headers?: Record<string, string>;
      errorMessage?: string;
      parseResponse?: boolean;
    } = {}
  ): Promise<T> {
    const { method = 'GET', body, rawBody, headers, errorMessage, parseResponse = true } = options;

    const fetchOptions: RequestInit = { method };

    if (body !== undefined) {
      fetchOptions.headers = { 'Content-Type': 'application/json', ...headers };
      fetchOptions.body = JSON.stringify(body);
    } else if (rawBody !== undefined) {
      fetchOptions.body = rawBody;
      if (headers) fetchOptions.headers = headers;
    } else {
      if (headers) fetchOptions.headers = headers;
    }

    const response = await fetch(`${this.baseUrl}${path}`, fetchOptions);

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(
        errorBody?.error ||
          (errorMessage
            ? `${errorMessage} (${response.status})`
            : `HTTP error! status: ${response.status}`)
      );
    }

    if (!parseResponse) return undefined as T;
    return response.json();
  }

  async sendChatMessage(
    request: ChatRequest,
    onChunk?: (chunk: string) => void,
    agentContext?: {
      calendarEvents?: any[];
      journalNotes?: any[];
      chatSummaries?: any[];
      activePage?: string;
      siteMap?: any[];
      longTermHealthMemory?: HealthMemoryContextSnippet[];
    },
    signal?: AbortSignal
  ): Promise<ChatResponse & { actions?: AgentAction[] }> {
    const isAgentMode = this.isAgentModeEnabled();

    // If agent mode is enabled, use the agent endpoint
    if (isAgentMode) {
      const agentPayload: any = {
        message: request.message,
        history: request.history?.map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.content
        })),
        journalData: request.journalData,
        threadSummary: request.threadSummary,
        stream: Boolean(onChunk),
        language: request.language,
      };

      if (request.assessmentState?.status === 'active') {
        agentPayload.assessmentState = request.assessmentState;
      }
      if (request.systemInstruction) {
        agentPayload.systemInstruction = request.systemInstruction;
      }
      if (request.requestedMode) {
        agentPayload.requestedMode = request.requestedMode;
      }

      const ctx = this.buildAgentContextFields(agentContext);
      agentPayload.calendarEvents = ctx.calendarEvents;
      agentPayload.journalNotes = ctx.journalNotes;
      agentPayload.chatSummaries = ctx.chatSummaries;
      agentPayload.activePage = ctx.activePage;
      agentPayload.siteMap = ctx.siteMap;
      agentPayload.longTermHealthMemory = ctx.longTermHealthMemory;

      logger.debug('[API] Sending to agent endpoint', {
        messageLength: agentPayload.message?.length,
        historyLength: agentPayload.history?.length,
        hasJournalData: !!agentPayload.journalData,
        journalDataEntries: agentPayload.journalData?.entries?.length ?? 0,
        journalDataKeys: agentPayload.journalData ? Object.keys(agentPayload.journalData) : [],
      });

      try {
        const response = await fetch(`${this.baseUrl}/api/agent/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(agentPayload),
          signal,
        });

        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}));
          throw new Error(errorBody?.error || `HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('content-type');
        if (contentType?.includes('text/event-stream')) {
          return this.handleStreamResponse(response, onChunk, signal);
        }

        const rawData = await response.json();
        const parsed = unifiedAgentResponseSchema.safeParse(rawData);
        const data = parsed.success ? parsed.data : {};

        return {
          response: data.assistant_message || data.reply || '',
          assistant_message: data.assistant_message || data.reply || '',
          timestamp: data.timestamp || new Date().toISOString(),
          actions: (data.actions || []) as AgentAction[],
          ui_blocks: data.ui_blocks || [],
          suggested_followups: data.suggested_followups || [],
          citations: data.citations,
          safety_notice: data.safety_notice,
          debug: data.debug,
        };
      } catch (error) {
        logger.error('Agent API call failed', { error });
        throw error;
      }
    }

    // Normal chat mode
    const payload: ChatRequest = {
      purpose: 'chat',
      ...request,
      language: request.language,
    };

    const preferredModel = this.getPreferredModel();
    if (preferredModel) {
      payload.model = preferredModel;
    } else if (payload.model === 'auto') {
      delete payload.model;
    }

    try {
      const response = await fetch(`${this.baseUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal,
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody?.error || `HTTP error! status: ${response.status}`);
      }

      // Check if response is streaming (text/event-stream)
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('text/event-stream')) {
        return this.handleStreamResponse(response, onChunk, signal);
      }

      // Fallback to JSON response
      const data = await response.json();
      return data;
    } catch (error) {
      logger.error('API call failed', { error });
      throw error;
    }
  }

  async startAssessment(
    agentContext?: {
      history?: ChatMessage[];
      journalData?: EIRData;
      calendarEvents?: any[];
      journalNotes?: any[];
      activePage?: string;
      siteMap?: any[];
      longTermHealthMemory?: HealthMemoryContextSnippet[];
    }
  ): Promise<ChatResponse & { actions?: AgentAction[] }> {
    const assessmentPayload: any = {
      message: 'Start a health assessment flow.',
      history: agentContext?.history?.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.content
      })) || [],
      journalData: agentContext?.journalData,
    };

    const ctx = this.buildAgentContextFields(agentContext);
    assessmentPayload.calendarEvents = ctx.calendarEvents;
    assessmentPayload.journalNotes = ctx.journalNotes;
    assessmentPayload.activePage = ctx.activePage;
    assessmentPayload.siteMap = ctx.siteMap;
    assessmentPayload.longTermHealthMemory = ctx.longTermHealthMemory;

    try {
      const response = await fetch(`${this.baseUrl}/api/agent/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(assessmentPayload)
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody?.error || `HTTP error! status: ${response.status}`);
      }

      const rawData = await response.json();
      const parsed = unifiedAgentResponseSchema.safeParse(rawData);
      const data = parsed.success ? parsed.data : {};

      return {
        response: data.assistant_message || data.reply || '',
        assistant_message: data.assistant_message || data.reply || '',
        timestamp: data.timestamp || new Date().toISOString(),
        actions: (data.actions || []) as AgentAction[],
        ui_blocks: data.ui_blocks || [],
        suggested_followups: data.suggested_followups || [],
        citations: data.citations,
        safety_notice: data.safety_notice,
        debug: data.debug,
      };
    } catch (error) {
      logger.error('Assessment API call failed', { error });
      throw error;
    }
  }

  async confirmAgentAction(action: AgentAction): Promise<{ action: AgentAction; executed: boolean; timestamp: string }> {
    const data = await this.request<{ action: AgentAction; executed: boolean; timestamp: string }>(
      '/api/agent/action/confirm',
      { method: 'POST', body: { action } }
    );
    return {
      action: data.action as AgentAction,
      executed: Boolean(data.executed),
      timestamp: data.timestamp || new Date().toISOString(),
    };
  }

  async extractConditions(
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
    threadId?: string
  ): Promise<{ conditions: HealthMemoryItem[] }> {
    const data = await this.request<{ conditions: HealthMemoryItem[] }>(
      '/api/extract-conditions',
      { method: 'POST', body: { messages, threadId } }
    );
    const conditions = Array.isArray(data.conditions) ? (data.conditions as HealthMemoryItem[]) : [];
    return { conditions };
  }

  private buildAgentContextFields(agentContext?: {
    calendarEvents?: any[];
    journalNotes?: any[];
    chatSummaries?: any[];
    activePage?: string;
    siteMap?: any[];
    longTermHealthMemory?: HealthMemoryContextSnippet[];
  }): {
    calendarEvents: any[];
    journalNotes: any[];
    chatSummaries: any[];
    activePage: string;
    siteMap: any[];
    longTermHealthMemory: HealthMemoryContextSnippet[];
  } {
    return {
      calendarEvents: agentContext?.calendarEvents || [],
      journalNotes: agentContext?.journalNotes || [],
      chatSummaries: agentContext?.chatSummaries || [],
      activePage: agentContext?.activePage || 'timeline',
      siteMap: agentContext?.siteMap || [],
      longTermHealthMemory: agentContext?.longTermHealthMemory || [],
    };
  }

  private extractStreamFinalData(data: Record<string, unknown>): {
    actions: AgentAction[] | undefined;
    blocks: ChatBlock[] | undefined;
    followups: string[] | undefined;
    citations: Array<{ entryId: string; reference: string }> | undefined;
    safetyNotice: string | undefined;
    debug: unknown;
  } {
    return {
      actions: Array.isArray(data.actions) ? (data.actions as AgentAction[]) : undefined,
      blocks: Array.isArray(data.ui_blocks) ? (data.ui_blocks as ChatBlock[]) : undefined,
      followups: Array.isArray(data.suggested_followups)
        ? data.suggested_followups.filter((item: unknown): item is string => typeof item === 'string')
        : undefined,
      citations: Array.isArray(data.citations)
        ? data.citations.filter((item: unknown): item is { entryId: string; reference: string } =>
            item != null && typeof (item as any).entryId === 'string' && typeof (item as any).reference === 'string'
          )
        : undefined,
      safetyNotice: typeof data.safety_notice === 'string' ? data.safety_notice : undefined,
      debug: data.debug,
    };
  }

  private async handleStreamResponse(
    response: Response,
    onChunk?: (chunk: string) => void,
    signal?: AbortSignal
  ): Promise<ChatResponse & { actions?: AgentAction[] }> {
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      throw new Error('Response body is not readable');
    }

    let fullResponse = '';
    let buffer = '';
    let receivedDone = false;
    let finalActions: AgentAction[] | undefined;
    let finalBlocks: ChatBlock[] | undefined;
    let finalFollowups: string[] | undefined;
    let finalCitations: Array<{ entryId: string; reference: string }> | undefined;
    let finalSafetyNotice: string | undefined;
    let finalDebug: unknown;

    try {
      while (true) {
        if (signal?.aborted) {
          throw new DOMException('Stream aborted', 'AbortError');
        }
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.trim()) continue; // Skip empty lines
          
          if (line.startsWith('data: ')) {
            try {
              const jsonStr = line.slice(6).trim();
              if (!jsonStr) continue;
              
              const data = JSON.parse(jsonStr);
              
              if (data.error) {
                throw new Error(data.error || 'Streaming error');
              }
              
              if (data.chunk) {
                fullResponse += data.chunk;
                if (onChunk) {
                  onChunk(data.chunk);
                }
              }
              
              if (data.done) {
                receivedDone = true;
                const extracted = this.extractStreamFinalData(data);
                finalActions = extracted.actions;
                finalBlocks = extracted.blocks;
                finalFollowups = extracted.followups;
                finalCitations = extracted.citations;
                finalSafetyNotice = extracted.safetyNotice;
                finalDebug = extracted.debug;
                // Use the full response from the done message if available
                // But don't process chunks after done - just return
                if (data.response && fullResponse.length === 0) {
                  // Only use data.response if we didn't receive any chunks
                  fullResponse = data.response;
                }
                return {
                  response: fullResponse,
                  assistant_message: fullResponse,
                  timestamp: (data.timestamp as string) || new Date().toISOString(),
                  actions: extracted.actions,
                  ui_blocks: extracted.blocks,
                  suggested_followups: extracted.followups,
                  citations: extracted.citations,
                  safety_notice: extracted.safetyNotice,
                  debug: extracted.debug,
                };
              }
            } catch (e) {
              // Ignore JSON parse errors for incomplete data, but log in dev
              logger.debug('Failed to parse SSE data', { error: e });
            }
          }
        }
      }

      // Handle any remaining buffer
      if (buffer.trim() && buffer.startsWith('data: ')) {
        try {
          const jsonStr = buffer.slice(6).trim();
          const data = JSON.parse(jsonStr);
          if (data.done) {
            receivedDone = true;
            const extracted = this.extractStreamFinalData(data);
            return {
              response: data.response || fullResponse,
              assistant_message: data.response || fullResponse,
              timestamp: (data.timestamp as string) || new Date().toISOString(),
              actions: extracted.actions,
              ui_blocks: extracted.blocks,
              suggested_followups: extracted.followups,
              citations: extracted.citations,
              safety_notice: extracted.safetyNotice,
              debug: extracted.debug,
            };
          }
        } catch (e) {
          // Ignore parse errors
        }
      }

      // If we didn't receive a done message but stream ended, return what we have
      if (!receivedDone && fullResponse.length === 0) {
        logger.warn('Stream ended without content or done message');
      }

      return {
        response: fullResponse || 'No response received',
        assistant_message: fullResponse || 'No response received',
        timestamp: new Date().toISOString(),
        actions: finalActions,
        ui_blocks: finalBlocks,
        suggested_followups: finalFollowups,
        citations: finalCitations,
        safety_notice: finalSafetyNotice,
        debug: finalDebug,
      };
    } catch (error) {
      logger.error('Error handling stream', { error });
      throw error;
    } finally {
      reader.releaseLock();
    }
  }

  async healthCheck(): Promise<{ status: string; timestamp: string; uptime: number }> {
    try {
      return await this.request<{ status: string; timestamp: string; uptime: number }>(
        '/health',
        { errorMessage: 'Health check failed' }
      );
    } catch (error) {
      logger.error('Health check failed', { error });
      throw error;
    }
  }

  async transcribeJournalAudio(audioBlob: Blob): Promise<TranscriptionResponse> {
    const base64Audio = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          const base64 = reader.result.split(',')[1];
          if (base64) {
            resolve(base64);
          } else {
            reject(new Error('Failed to read audio data.'));
          }
        } else {
          reject(new Error('Failed to convert audio.'));
        }
      };
      reader.onerror = () => reject(new Error('Unable to read audio data.'));
      reader.readAsDataURL(audioBlob);
    });

    return this.request<TranscriptionResponse>('/api/journal/transcribe', {
      method: 'POST',
      body: {
        audio: {
          data: base64Audio,
          mimeType: audioBlob.type || 'audio/webm'
        }
      },
    });
  }

  async analyzeMealImage(payload: {
    data: string;
    mimeType: string;
  }): Promise<NutritionAnalysisResponse> {
    return this.request<NutritionAnalysisResponse>('/api/nutrition/analyze', {
      method: 'POST',
      body: { image: payload },
    });
  }

  async analyzeHealth(journalData: EIRData): Promise<HealthAnalysis> {
    const data = await this.request<HealthAnalysisResponse>('/api/health-analysis', {
      method: 'POST',
      body: { journalData },
    });
    return {
      ...data.analysis,
      generatedAt: data.metadata?.generatedAt,
      model: data.metadata?.model,
    };
  }

  async generateCareRequest(payload: CareRequestInput): Promise<CareRequestPlanResponse> {
    return this.request<CareRequestPlanResponse>('/api/care-request', {
      method: 'POST',
      body: payload,
      errorMessage: 'Unable to generate care request',
    });
  }

  async sendCareContact(payload: CareContactRequest): Promise<CareContactResponse> {
    return this.request<CareContactResponse>('/api/care-contact', {
      method: 'POST',
      body: payload,
      errorMessage: 'Unable to send care contact',
    });
  }

  async getCareSuggestions(payload: CareSuggestRequest): Promise<CareSuggestResponse> {
    return this.request<CareSuggestResponse>('/api/care-contact/suggest', {
      method: 'POST',
      body: payload,
      errorMessage: 'Unable to get care suggestions',
    });
  }

  async startRemoteEhrSession(): Promise<RemoteEhrSessionStartResponse> {
    return this.request<RemoteEhrSessionStartResponse>('/api/ehr/start', {
      method: 'POST',
      errorMessage: 'Unable to start BankID session',
    });
  }

  async getRemoteEhrStatus(sessionId: string): Promise<RemoteEhrSessionStatusResponse> {
    return this.request<RemoteEhrSessionStatusResponse>(
      `/api/ehr/session/${sessionId}/status`,
      { errorMessage: 'Unable to check session status' }
    );
  }

  async getRemoteEhrQr(sessionId: string): Promise<{ qrImage: string }> {
    return this.request<{ qrImage: string }>(
      `/api/ehr/session/${sessionId}/qr`,
      { errorMessage: 'Unable to refresh BankID QR code' }
    );
  }

  async getRemoteEhrData(sessionId: string): Promise<EIRData> {
    return this.request<EIRData>(
      `/api/ehr/session/${sessionId}/data`,
      { errorMessage: 'Unable to load journal data' }
    );
  }

  // Fetch backend-generated .eir (YAML) and return as text
  async getRemoteEhrEirYaml(sessionId: string): Promise<string> {
    const response = await fetch(`${this.baseUrl}/api/ehr/session/${sessionId}/export/eir`);
    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      throw new Error(errorBody || `Unable to load EIR export (${response.status})`);
    }
    return response.text();
  }

  async cancelRemoteEhrSession(sessionId: string): Promise<void> {
    await this.request<void>(
      `/api/ehr/session/${sessionId}/cancel`,
      { method: 'POST', errorMessage: 'Unable to cancel session', parseResponse: false }
    );
  }

  async getMobileAutostartToken(sessionId: string): Promise<{
    autoStartToken: string;
    orderRef: string | null;
    redirectUrl: string;
    expiresAt: string;
  } | null> {
    const response = await fetch(`${this.baseUrl}/api/ehr/session/${sessionId}/mobile-autostart`);

    // 404 means token not yet available (pending) - this is expected
    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      const message = errorBody?.error || `Unable to get mobile autostart token (${response.status})`;
      throw new Error(message);
    }

    return response.json();
  }

  async getHealthVideos(payload: HealthVideosRequest): Promise<HealthVideosResponse> {
    return this.request<HealthVideosResponse>('/api/health-videos', {
      method: 'POST',
      body: payload,
    });
  }

  async generateChatSummary(messages: ChatMessage[]): Promise<{ summary: string; transcript: string; aiDescription: string; timestamp: string }> {
    const preferredModel = this.getPreferredModel();
    const body: Record<string, unknown> = { messages };

    if (preferredModel) {
      body.model = preferredModel;
    }

    return this.request<{ summary: string; transcript: string; aiDescription: string; timestamp: string }>(
      '/api/chat/summary',
      { method: 'POST', body }
    );
  }

  async generateHealthActions(query: string | null, journalData: EIRData | null): Promise<{ actions: HealthAction[]; generatedAt: string; source: string }> {
    return this.request<{ actions: HealthAction[]; generatedAt: string; source: string }>(
      '/api/health-actions',
      { method: 'POST', body: { query, journalData } }
    );
  }

  /**
   * Get community actions (sorted by votes)
   */
  async getCommunityActions(category?: string, limit?: number): Promise<{ success: boolean; actions: HealthAction[] }> {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (limit) params.append('limit', limit.toString());

    return this.request<{ success: boolean; actions: HealthAction[] }>(
      `/api/community-actions?${params.toString()}`
    );
  }

  /**
   * Publish a health action to the community
   */
  async publishCommunityAction(action: HealthAction, authorEmail?: string): Promise<{ success: boolean; action: HealthAction; message: string }> {
    return this.request<{ success: boolean; action: HealthAction; message: string }>(
      '/api/community-actions',
      {
        method: 'POST',
        body: {
          title: action.title,
          description: action.description,
          category: action.category,
          estimatedTime: action.estimatedTime,
          difficulty: action.difficulty,
          benefits: action.benefits,
          instructions: action.instructions,
          tips: action.tips,
          authorEmail,
        },
      }
    );
  }

  /**
   * Vote on a community action (toggle vote)
   */
  async voteCommunityAction(actionId: string): Promise<{ success: boolean; voted: boolean; votesCount: number }> {
    return this.request<{ success: boolean; voted: boolean; votesCount: number }>(
      `/api/community-actions/${actionId}/vote`,
      { method: 'POST' }
    );
  }

  /**
   * Check if user has voted on an action
   */
  async getVoteStatus(actionId: string): Promise<{ success: boolean; hasVoted: boolean }> {
    return this.request<{ success: boolean; hasVoted: boolean }>(
      `/api/community-actions/${actionId}/vote-status`
    );
  }

  /**
   * Create a Stripe Checkout session for subscription
   */
  async createCheckoutSession(email: string | null, plan: 'monthly' | 'annual'): Promise<CheckoutSessionResponse> {
    return this.request<CheckoutSessionResponse>(
      '/api/subscriptions/create-checkout-session',
      { method: 'POST', body: { email, plan } }
    );
  }

  /**
   * Get subscription status by email
   */
  async getSubscriptionStatus(email: string): Promise<SubscriptionStatusResponse> {
    return this.request<SubscriptionStatusResponse>(
      `/api/subscriptions/status?email=${encodeURIComponent(email)}`
    );
  }

  /**
   * Verify a checkout session after payment
   */
  async verifyCheckoutSession(sessionId: string): Promise<SubscriptionVerifyResponse> {
    return this.request<SubscriptionVerifyResponse>(
      `/api/subscriptions/verify-session?session_id=${encodeURIComponent(sessionId)}`
    );
  }

  /**
   * Create a Stripe Customer Portal session for billing management
   */
  async submitFeedback(feedback: {
    rating: number;
    message: string;
    contactEmail?: string;
    contactPhone?: string;
  }): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>('/api/feedback', {
      method: 'POST',
      body: feedback,
    });
  }

  async createFeedbackForm(input: { title?: string } = {}): Promise<FeedbackFormSummary> {
    const data = await this.request<{ form: FeedbackFormSummary }>(
      '/api/feedback/forms',
      { method: 'POST', body: input }
    );
    return data.form;
  }

  async getFeedbackFormDetails(formId: string): Promise<FeedbackFormDetails> {
    const data = await this.request<{ form: FeedbackFormSummary; insights: FeedbackInsight[]; actions: FeedbackActionPlan[] }>(
      `/api/feedback/forms/internal/${encodeURIComponent(formId)}`
    );
    return {
      ...data.form,
      insights: data.insights,
      actions: data.actions,
    };
  }

  async deleteFeedbackForm(formId: string): Promise<void> {
    await this.request<void>(
      `/api/feedback/forms/internal/${encodeURIComponent(formId)}`,
      { method: 'DELETE', parseResponse: false }
    );
  }

  async getPublicFeedbackForm(externalId: string): Promise<PublicFeedbackForm> {
    const data = await this.request<{ form: PublicFeedbackForm }>(
      `/api/feedback-studio/forms/public/${encodeURIComponent(externalId)}`
    );
    return data.form;
  }

  async transformFeedbackForStudio(request: FeedbackTransformRequest): Promise<{ insights: FeedbackInsight[]; actions: FeedbackActionPlan[] }> {
    const data = await this.request<{ insights: FeedbackInsight[]; actions: FeedbackActionPlan[] }>(
      '/api/feedback-studio/transform',
      { method: 'POST', body: request }
    );
    return {
      insights: data.insights,
      actions: data.actions,
    };
  }

  async submitFeedbackToForm(
    externalId: string,
    submission: FeedbackSubmissionRequest
  ): Promise<FeedbackSubmissionResponse> {
    return this.request<FeedbackSubmissionResponse>(
      `/api/feedback-studio/forms/public/${encodeURIComponent(externalId)}/submissions`,
      { method: 'POST', body: submission }
    );
  }

  async createPortalSession(email: string): Promise<PortalSessionResponse> {
    return this.request<PortalSessionResponse>(
      '/api/subscriptions/create-portal-session',
      { method: 'POST', body: { email } }
    );
  }

  async generateCbtProgram(request: {
    journalData?: any;
    journalNotes?: any[];
    healthAssessments?: any[];
    chatSummaries?: any[];
    feedback?: any[];
    userPrompt: string;
  }): Promise<{ success: boolean; program: string; generatedAt: string }> {
    return this.request<{ success: boolean; program: string; generatedAt: string }>(
      '/api/generate-cbt-program',
      { method: 'POST', body: request }
    );
  }

  async publishCbtProgram(request: {
    programId: string;
    programYaml: string;
    title: string;
    condition?: string;
    summary?: string;
    duration?: string;
    focusAreas?: string[];
    authorEmail?: string;
  }): Promise<{ success: boolean; program: any }> {
    return this.request<{ success: boolean; program: any }>(
      '/api/publish-program',
      { method: 'POST', body: request }
    );
  }

  async unpublishCbtProgram(programId: string): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      '/api/unpublish-program',
      { method: 'POST', body: { programId } }
    );
  }

  async getCommunityPrograms(): Promise<{ success: boolean; programs: any[] }> {
    return this.request<{ success: boolean; programs: any[] }>('/api/community-programs');
  }

  async deleteCbtProgram(programId: string): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      '/api/delete-program',
      { method: 'DELETE', body: { programId } }
    );
  }

  // Roadmap API methods
  async getRoadmapFeatures(): Promise<{ success: boolean; features: RoadmapFeature[] }> {
    return this.request<{ success: boolean; features: RoadmapFeature[] }>('/api/roadmap/features');
  }

  async upvoteRoadmapFeature(featureId: string): Promise<RoadmapUpvoteResponse> {
    return this.request<RoadmapUpvoteResponse>(
      `/api/roadmap/features/${featureId}/upvote`,
      { method: 'POST' }
    );
  }

  async getRoadmapSuggestions(): Promise<{ success: boolean; suggestions: RoadmapSuggestion[] }> {
    return this.request<{ success: boolean; suggestions: RoadmapSuggestion[] }>('/api/roadmap/suggestions');
  }

  async createRoadmapSuggestion(request: RoadmapSuggestionRequest): Promise<{ success: boolean; suggestion: RoadmapSuggestion; message: string }> {
    return this.request<{ success: boolean; suggestion: RoadmapSuggestion; message: string }>(
      '/api/roadmap/suggestions',
      { method: 'POST', body: request }
    );
  }

  async upvoteRoadmapSuggestion(suggestionId: string): Promise<RoadmapUpvoteResponse> {
    return this.request<RoadmapUpvoteResponse>(
      `/api/roadmap/suggestions/${suggestionId}/upvote`,
      { method: 'POST' }
    );
  }

  async recommendCareFacility(healthIssue: string, facilities: Array<{
    id: string;
    name: string;
    description: string;
    tags: string[];
    distanceKm: number;
    address: string;
    city: string;
    phone?: string;
    website?: string;
  }>): Promise<{
    success: boolean;
    facility: typeof facilities[0];
    reason: string;
    aiResponse?: string;
  }> {
    return this.request<{
      success: boolean;
      facility: typeof facilities[0];
      reason: string;
      aiResponse?: string;
    }>('/api/agent/recommend-care', {
      method: 'POST',
      body: { healthIssue, facilities },
    });
  }

  async generateHealthQuiz(request: {
    journalData?: EIRData | null;
    journalNotes?: any[];
    chatSummaries?: any[];
  }): Promise<{ success: boolean; quiz: HealthQuiz }> {
    return this.request<{ success: boolean; quiz: HealthQuiz }>(
      '/api/health-quiz/generate',
      { method: 'POST', body: request }
    );
  }

  async getHealthQuiz(quizId: string): Promise<{ success: boolean; quiz: HealthQuiz }> {
    return this.request<{ success: boolean; quiz: HealthQuiz }>(
      `/api/health-quiz/${quizId}`
    );
  }

  async submitHealthQuiz(quizId: string, answers: number[]): Promise<{ success: boolean; result: QuizResult }> {
    return this.request<{ success: boolean; result: QuizResult }>(
      `/api/health-quiz/${quizId}/submit`,
      { method: 'POST', body: { answers } }
    );
  }

  async getHealthQuizResults(): Promise<{ success: boolean; results: QuizResult[] }> {
    return this.request<{ success: boolean; results: QuizResult[] }>('/api/health-quiz/results');
  }

  /**
   * Parse a document (PDF or image) and extract health records in EIR format.
   * Uses OCR first (Tesseract) and falls back to AI Vision for complex documents.
   */
  async parseDocument(file: File): Promise<{
    success: boolean;
    data: EIRData | null;
    method: 'ocr' | 'ai_vision';
    confidence: number;
    error?: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    return this.request<{
      success: boolean;
      data: EIRData | null;
      method: 'ocr' | 'ai_vision';
      confidence: number;
      error?: string;
    }>('/api/ehr/parse-document', {
      method: 'POST',
      rawBody: formData,
    });
  }
}

export const apiService = new ApiService();
